import classnames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDndScrolling } from 'react-dnd-scrolling';

import { AddContactAttributesModal } from 'components/contact';
import { SidebarToggleTrigger } from 'components/conversation/components/sidebar/sidebarToggleTrigger';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useConversationSidebarSettings } from 'utils/useConversationSidebarSettings';
import { useServerStorage } from 'utils/useServerStorage';

import { FlatSidebar } from './components/flatSidebar/flatSidebar';
import type { SidebarMode } from './conversationSidebar.types';
import { SidebarModeContext } from './hooks/useSidebarMode';

export const ConversationSidebar = (): JSX.Element => {
  const metaConversation = useAppSelector(
    (state) => state.conversation.metaConversation,
  );
  const { sidebarCardsAdminManagement } = useFeatureFlag([
    'sidebar-cards-admin-management',
  ]);
  const orgStorage = useServerStorage('organization');
  // Whether the agent may personalize. Under the admin-managed flag this is the
  // org's setting (defaulting to allowed, matching the settings page).
  const canCustomize =
    !sidebarCardsAdminManagement ||
    (orgStorage.data.conversation_sidebar?.allowAgentPersonalization ?? true);
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>('default');

  // Leave customization if personalization is turned off while it's open.
  useEffect(() => {
    if (!canCustomize && sidebarMode === 'customization') {
      setSidebarMode('default');
    }
  }, [canCustomize, sidebarMode]);
  const { cardOpenState, isSidebarOpen, setCardOpen, toggleSidebar } =
    useConversationSidebarSettings();

  const isCardOpen = useCallback(
    (key: string) => cardOpenState[key] ?? false,
    [cardOpenState],
  );

  const toggleCard = useCallback(
    (key: string) => {
      setCardOpen(key, !(cardOpenState[key] ?? false));
    },
    [cardOpenState, setCardOpen],
  );
  const rootClassName = classnames('conversation-sidebar-holder', {
    closed: !isSidebarOpen,
  });

  // Auto-scroll the sidebar while dragging a card near its top/bottom edge.
  const rootRef = useRef<HTMLDivElement>(null);
  useDndScrolling(rootRef);

  return (
    <div ref={rootRef} className={rootClassName}>
      <SidebarToggleTrigger
        isMenuOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        sidebarMode={sidebarMode}
        setSidebarMode={setSidebarMode}
        canCustomize={canCustomize}
      />
      <SidebarModeContext.Provider value={sidebarMode}>
        <FlatSidebar
          metaConversation={metaConversation}
          isMinimized={!isSidebarOpen}
          isCardOpen={isCardOpen}
          toggleCard={toggleCard}
        />
      </SidebarModeContext.Provider>
      <AddContactAttributesModal />
    </div>
  );
};
